/**
 * Configuration for model-specific animation settings.
 *
 * Optional per-model `fps` (Task #326): declares the frame-space this entry's
 * subclip ranges were authored in, so playback never depends on measuring the
 * file's keyframe density at load. Runtime precedence
 * (AnimationController._effectiveFps): server override fps → this bundled
 * fps → inferred from the clip. Only declare it where the ranges' authorship
 * is documented (see the per-model comments) — never guess; models without it
 * keep the historical inference behavior.
 */
export const AnimationConfig = {
    // Default configuration for all models
    default: {
        // "ALL" is the LAST candidate in every slot (Task #440): nearly every
        // current GLB export ships one single reel named "ALL", so a character
        // with no per-model entry and no server names map still finds its reel
        // (subclipped by the slot ranges) instead of resting in A-pose. Listed
        // last so real named takes (FBX exports) always win when present.
        animations: {
            idle: {
                names: ["Idle", "IdleLoop", "idle", "Idle_Loop", "ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["Listen", "Listening", "listen", "ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["Speak01", "Speaking01", "speak", "ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["Speak02", "Speaking02", "ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["Spawn", "spawn", "ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },

    // Richard Feynman model
    "bc5d31a8-047b-4f72-8f16-fea7a365ca69": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },

    // Erwin Schrodinger model
    "01db85dc-81e0-4433-9440-a54ce4976178": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 989,
                    end: 1067,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 420,
                    end: 959,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 420,
                    end: 959,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 258,
                    end: 400,
                },
            },
        },
    },

    // Claude Shannon model
        "2db1d398-0161-46cd-8aff-53008d29a9dc": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // Michael Faraday model
    "0c6099ed-c0db-4dd7-bc46-1d720324db5a": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 100,
                    end: 350,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 0,
                    end: 90,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 510,
                    end: 630,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 640,
                    end: 770,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 360,
                    end: 500,
                },
            },
        },
    },

    // Napoleon Bonaparte model
    "c2cc71ce-89e7-4c94-9441-eec86ec67a60": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 259,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 922,
                    end: 1000,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 411,
                    end: 906,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 411,
                    end: 906,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 276,
                    end: 676,
                },
            },
        },
    },

    // Alan Turing model (GLB — single "ALL" clip)
    "be0dcf0f-c72b-48f7-a462-6ba7560498d7": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // Friedrich Nietzsche model
    "88c314c4-5890-4e55-9418-8b46a376ef81a": {
        animations: {
            idle: {
                names: ["Action Retarget.001"],
                subclip: {
                    start: 100,
                    end: 250,
                },
            },
            listen: {
                names: ["Action Retarget.001"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["Action Retarget.001"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["Action Retarget.001"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["Action Retarget.001"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
        },
    },

    // John Neumann model
    "1a35136c-42d8-46c7-a189-c4b32da87832": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 100,
                    end: 350,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 0,
                    end: 90,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 520,
                    end: 640,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 650,
                    end: 770,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 360,
                    end: 510,
                },
            },
        },
    },

    // Nikola Tesla model (NikolaNewFaceGLBV2 — single "ALL" clip, 30fps,
    // ~1291 frames). Sheet timings: IdleLoop 0-150 (second idle 150-250),
    // Listen 250-450, Looksup 450-650 (unused), Spawn 650-850,
    // Speak01 850-1050, Speak02 1050-1250, Walk 1250-1300 (unused).
    "d537d61d-0313-421b-ae66-48a8d7222b3f": {
        // Ranges are authored on the 30fps full-template timeline documented
        // above (file measured 30 keys/s at declaration, 2026-08-11).
        fps: 30,
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // René Descartes model (GLB — single "ALL" clip)
    "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL", "Listen", "Listening"],
                subclip: {
                    start: 200,
                    end: 400,
                },
            },
            speak: {
                names: ["ALL", "Speak01", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speak02", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL", "Spawn"],
                subclip: {
                    start: 600,
                    end: 850,
                },
            },
        },
    },

    // Oppenheimer
    "5eb1ad8d-4c11-4174-b79a-c232991d015b": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // Isaac Newton model (NewtonNew — single "ALL" clip, 24fps, ~880 frames;
    // no sheet timings). Derived from motion-energy analysis: same segment
    // order as the 30fps sheet template, scaled to 24fps and truncated after
    // Speak01: IdleLoop 0-120, idle2 120-200, Listen 200-360,
    // Looksup 360-520 (unused), Spawn 520-680, Speak01 680-840,
    // dead/still frames 840+ (never reference). No Speak02 → speak2 reuses
    // Speak01 (Schrodinger/Napoleon precedent). VERIFY VISUALLY.
    "7021ffd6-51bb-41a8-920e-935899dcd631": {
        // Ranges are authored in the 24-space truncated template documented
        // above (file measured 24 keys/s at declaration, 2026-08-11).
        fps: 24,
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 120,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 200,
                    end: 360,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 520,
                    end: 680,
                },
            },
        },
    },

    // James Clerk Maxwell model
    "93ea7051-9722-443d-90fb-18a84c51894c": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },

    //Ernest Hemingway model
    "b20dc26f-8924-4aee-9ef5-9b98c0367a34": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },

    // Charles Darwin model (DarwinNew — single "ALL" clip, 24fps, ~920
    // frames; no sheet timings). Derived from motion-energy analysis (same
    // 24fps layout as NewtonNew/EinstineNewGLB): IdleLoop 0-120,
    // idle2 120-200, Listen 200-360, Looksup 360-520 (unused),
    // Spawn 520-680, Speak01 680-840, dead/still frames 840+ (never
    // reference). No Speak02 → speak2 reuses Speak01. VERIFY VISUALLY.
    "897eb00c-4f16-4f73-81d5-d1c91fad53f2": {
        // Ranges are authored in the 24-space truncated template documented
        // above (file measured 24 keys/s at declaration, 2026-08-11).
        fps: 24,
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 120,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 200,
                    end: 360,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 520,
                    end: 680,
                },
            },
        },
    },

    // Abraham Lincoln
    "031f2eea-64e5-441d-a37d-03d260cc43dc": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // Martin Luther King model (named takes on a global 30fps timeline,
    // derived from FBX parse 2026-08-10: Speaking01 0-250, Speaking02 250-500,
    // Listening 500-710, Idle 710-920, Spawn 920-1180, Walk 1180-1240)
    "d0c97034-328e-4538-8107-a9825d0c1594": {
        // Named takes sit on a global 30fps timeline (FBX parse 2026-08-10;
        // re-verified 2026-08-11: every take keys at exactly 30/s).
        fps: 30,
        animations: {
            idle: {
                names: ["ALL", "Idle"],
                subclip: {
                    start: 710,
                    end: 920,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 500,
                    end: 710,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 0,
                    end: 250,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 250,
                    end: 500,
                },
            },
            spawn: {
                names: ["ALL", "Spawn"],
                subclip: {
                    start: 920,
                    end: 1180,
                },
            },
        },
    },
    // Karl Marx model
    "4d7d2e17-b818-4144-b78b-629c688a71ac": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // Niccolo Machiavelli model
    "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // Adam Smith
    "a59fe2c1-9046-405a-9a99-a7ddbaee13be": {
        animations: {
            idle: {
                names: ["ALL", "Idle"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL", "listen"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "speak"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL", "spawn"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Albert Einstein model (EinstineNewGLB — single "ALL" clip, 24fps,
    // ~900 frames; no sheet timings). Derived from motion-energy analysis
    // (same 24fps layout as NewtonNew/DarwinNew): IdleLoop 0-120,
    // idle2 120-200, Listen 200-360, Looksup 360-520 (unused),
    // Spawn 520-680, Speak01 680-840, dead/still frames 840+ (never
    // reference). No Speak02 → speak2 reuses Speak01. VERIFY VISUALLY.
    "278dddd6-b4a8-4303-a41d-be6219dbc907": {
        // Ranges are authored in the 24-space truncated template documented
        // above (file measured 24 keys/s at declaration, 2026-08-11). The
        // live server override (fps 30, re-slotted ranges — the 2026-08-11
        // lip-sync fix) still wins over this at runtime.
        fps: 24,
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 120,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 200,
                    end: 360,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 520,
                    end: 680,
                },
            },
        },
    },
    // Mansa Musa model
    "3f14429d-b2d4-4b99-8a05-58eed567c0d3": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 150,
                    end: 250, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // Confucius (GLB — single "ALL" clip)
    "b8951237-698b-42b5-8b6e-ee381b91fb65": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL", "Listen", "Listening"],
                subclip: {
                    start: 200,
                    end: 400,
                },
            },
            speak: {
                names: ["ALL", "Speak01", "Speaking01"],
                subclip: {
                    start: 600,
                    end: 800,
                },
            },
            speak2: {
                names: ["ALL", "Speak02", "Speaking02"],
                subclip: {
                    start: 800,
                    end: 1000,
                },
            },
            spawn: {
                names: ["ALL", "Spawn"],
                subclip: {
                    start: 400,
                    end: 600,
                },
            },
        },
    },
    // Amelia Earhart model with optimized animations and extended patterns
    "770e2d93-a6a4-41ed-aca1-8951327885b5": {
        animations: {
            idle: {
                names: ["ALL", "Idle"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL", "listen"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "speak"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL", "spawn"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Henry Ford model with comprehensive animation configuration
    "5a16ff56-6e03-414e-a89d-cda9d83fae60": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1248,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    //Marie
    "73f610bf-d9c9-4d9f-a026-a10086a66725": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 150,
                    end: 250, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    //Niels Bhor
    "802bd089-d231-4c4e-830c-476199321af8": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // Van Gogh
    "992e9007-b4f8-4711-80c2-884daf3d9960": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 120,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 200,
                    end: 360,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 520,
                    end: 680,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 360,
                    end: 520,
                },
            },
        },
    },

    // Al Capone
    "e3f55c33-3ff0-471f-b833-b59d7b145fc6": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 150,
                    end: 250, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // M ali model
    "2baa0479-7e38-4473-a2f2-1ad54149dbac": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // Warren Buffett model
    // Euclid
    "7c7ac397-c36e-4218-92d4-f437d1959cca": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 100,
                    end: 300,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 750,
                    end: 950,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 950,
                    end: 1150,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 500,
                    end: 750,
                },
            },
        },
    },
    // Srinivasa Ramanujan
    "f9f3f836-00c6-4160-8416-4b71b53f1307": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 100,
                    end: 300,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 700,
                    end: 900,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 900,
                    end: 1100,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 500,
                    end: 700,
                },
            },
        },
    },
    // Blaise Pascal
    "5802e813-e715-491d-a0fd-92dc643c458e": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 100,
                    end: 300,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 700,
                    end: 900,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 900,
                    end: 1100,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 500,
                    end: 700,
                },
            },
        },
    },
    // Jacob Bernoulli
    "191e6103-a42c-4132-926c-230e7b1be5b6": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // Al-Khwarizmi
    "6bcf3678-e232-46c9-996a-67beb3fd2dbf": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Socrates
    "14836d13-ace6-4bf8-be26-26fbb80ed9bc": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Sigmund Freud
    "3841a6bb-f977-453d-92f5-6cec1267546f": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
        },
    },
    // Ada Lovelace
    "c0063a05-916b-45d0-bc2c-c63210414b17": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Carl Friedrich Gauss
    "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // Galileo Galilei
    "127cd993-f16e-4659-84fa-882cba633097": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Grace Hopper
    "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Joseph-Louis Lagrange
    "c93478a1-ed2f-4f1a-81f6-3fa474a21566": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // Leonhard Euler
    "5a39bed2-a551-4a03-98a2-46104a3324bf": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // Brahmagupta
    "2707ca40-df64-4258-97c8-67d3695e8a42": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 249,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 249,
                    end: 449,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 699,
                    end: 949,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 949,
                    end: 1159,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 449,
                    end: 699,
                },
            },
        },
    },
    // Benjamin Franklin
    "e617290b-bda0-4771-91ac-3fe6939dd412": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Omar Khayyam
    "a2a43496-9a83-4444-813e-18a1930296e6": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Diophantus
    "78376372-3d71-4fce-8650-82b5be2c006b": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 249,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 249,
                    end: 449,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 699,
                    end: 949,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 949,
                    end: 1159,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 449,
                    end: 699,
                },
            },
        },
    },
    // William Playfair
    "ca6ebdca-5151-4a58-8523-56d25fce5dfa": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // George Boole
    "e3428624-bd1e-48b9-ae8c-c66ba68879cc": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 100,
                    end: 300,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 700,
                    end: 900,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 900,
                    end: 1100,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 500,
                    end: 700,
                },
            },
        },
    },
    // Thomas Bayes
    "3140d634-c7ca-4bda-af36-e1b49978b201": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // Eratosthenes
    "c73fed54-81e3-46ec-9d7c-3d11d05b142d": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Alessandro Volta
    "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 100,
                    end: 300,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 700,
                    end: 900,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 900,
                    end: 1100,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 500,
                    end: 700,
                },
            },
        },
    },
    // Archimedes
    "d13b5a81-ed77-4a25-8a01-a76a0165b349": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // François Viète
    "8f67d579-41c2-456d-900b-fccbcbf46bd3": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 860,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 860,
                    end: 1110,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    // Felix Klein
    "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 100,
                    end: 300,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 700,
                    end: 900,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 900,
                    end: 1100,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 500,
                    end: 700,
                },
            },
        },
    },
    "44f87c0b-ca22-4225-9fea-8d823e6a0160": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 120,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 200,
                    end: 360,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 520,
                    end: 680,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 680,
                    end: 840,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 360,
                    end: 520,
                },
            },
        },
    },
    // Salvador Dali model
    "d75e63e0-2b2b-43ef-933f-589e8d6818c0": {
        animations: {
            idle: {
                names: ["ALL", "Idle"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "listen"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "speak"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL", "spawn"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Gorge Orwell model
    "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 150,
                    end: 250, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },
    // Leonardo da Vinci model
    "8944783c-2e2b-4796-b92e-21dacca776bc": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150, // Changed to match the actual animation length in model
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Plato model
    "3542f19e-4e07-4828-8f9d-f330e4d6d660": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    // Darwin model
    "d82bc58b-5487-4af2-ad5e-fa9fa838e239": {
        animations: {
            idle: {
                names: ["Idle", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["Listen", "Listening"],
                subclip: {
                    start: 550,
                    end: 750,
                },
            },
            speak: {
                names: ["Speak01", "Speaking01"],
                subclip: {
                    start: 750,
                    end: 1000,
                },
            },
            speak2: {
                names: ["Speak02", "Speaking02"],
                subclip: {
                    start: 1000,
                    end: 1200,
                },
            },
            spawn: {
                names: ["Spawn"],
                subclip: {
                    start: 300,
                    end: 550,
                },
            },
        },
    },
    // Aristotle model
        "c4b9ac58-a66a-451e-b035-afabd8be50b9": {
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 200,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 200,
                    end: 360,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 560,
                    end: 760,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 760,
                    end: 928,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 360,
                    end: 560,
                },
            },
        },
    },
    // Marcus Aurelius model (MarcusGLBV4 — single "ALL" clip, 30fps,
    // 43.37s ≈ 1301 frames; measured 2026-08-11). Tester-verified ranges,
    // mirrored from the live animation override so a lost Firestore doc can
    // never revert him to generic ranges: Idle 0-150 (a 150-250 second idle
    // take plays via the server idleSequence policy), Listen 250-450,
    // LookUp 450-650, Spawn 650-850, Speak 850-1050, Speak2 1050-1250.
    "e97568c5-1daa-4a95-b4ae-0fe374c6b083": {
        // V4 keys at 30/s (measured) and the ranges were authored on that
        // timeline — declared so a re-export can't silently shift them.
        fps: 30,
        animations: {
            idle: {
                names: ["ALL"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            lookup: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["ALL"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
    //Cyrus
    "9ef81498-3fb9-4175-a6d4-c2a02d71c640": {
        animations: {
            idle: {
                names: ["Idle", "Idle_Loop"],
                subclip: {
                    start: 0,
                    end: 100,
                },
            },
            listen: {
                names: ["Listen", "Listening"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
            speak: {
                names: ["Speak01", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["Speak02", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["Spawn"],
                subclip: {
                    start: 200,
                    end: 450,
                },
            },
        },
    },
    //Genghis Khan
    "581e7293-43cf-45ea-9fb3-0955c563cbe2": {
        animations: {
            idle: {
                names: ["ALL", "Idle_Loop"],
                subclip: {
                    start: 150,
                    end: 250,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 450,
                    end: 650,
                },
            },
        },
    },

    //Enrico Fermi
    "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13": {
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // Frida Kahlo model (single "ALL" clip, 30fps ~1300 frames; layout from
    // designer-supplied timings, June 2026 — same 30fps full template as
    // Nikola/Nostradamus: IdleLoop 0-250, Listen 250-450, Looksup 450-650
    // (unused), Spawn 650-850, Speak01 850-1050, Speak02 1050-1250)
    "4f50a698-6034-49c1-b9d4-221f59bf2378": {
        // Ranges are authored on that 30fps timeline (file measured
        // 30 keys/s at declaration, 2026-08-11).
        fps: 30,
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },

    // Nostradamus model (single "ALL" clip, 30fps ~1291 frames; layout from
    // designer-supplied timings, June 2026 — same 30fps full template as
    // Nikola/Frida: IdleLoop 0-250, Listen 250-450, Looksup 450-650 (unused),
    // Spawn 650-850, Speak01 850-1050, Speak02 1050-1250)
    "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": {
        // Ranges are authored on that 30fps timeline (file measured
        // 30 keys/s at declaration, 2026-08-11).
        fps: 30,
        animations: {
            idle: {
                names: ["ALL", "IdleLoop"],
                subclip: {
                    start: 0,
                    end: 150,
                },
            },
            listen: {
                names: ["ALL", "Listening"],
                subclip: {
                    start: 250,
                    end: 450,
                },
            },
            speak: {
                names: ["ALL", "Speaking01"],
                subclip: {
                    start: 850,
                    end: 1050,
                },
            },
            speak2: {
                names: ["ALL", "Speaking02"],
                subclip: {
                    start: 1050,
                    end: 1250,
                },
            },
            spawn: {
                names: ["ALL"],
                subclip: {
                    start: 650,
                    end: 850,
                },
            },
        },
    },
};

/**
 * Find the correct animation clip based on model ID and animation type
 * @param {Array} availableClips - Available animation clips in the model
 * @param {String} modelId - UUID of the model
 * @param {String} animationType - Type of animation (idle, listen, speak, spawn)
 * @returns {Object} - Found animation clip and its subclip configuration
 */
// ─── Runtime animation overrides (Task #300 follow-on) ─────────────────────
// Server-stored per-model overrides (Firestore `animationOverrides`, served by
// /api/animations/<uuid>) fetched by ModelViewer at model load. They take
// precedence over the bundled entries above WITHOUT a rebuild — managed from
// the /admin/animations panel or `scripts/legendctl.mjs`. Empty by default,
// so every surface behaves exactly as before unless an override exists.
const runtimeAnimationOverrides = {};

export function setRuntimeAnimationOverride(modelId, effective) {
    if (!modelId || !effective || typeof effective !== "object") return;
    runtimeAnimationOverrides[modelId] = effective;
}

export function getRuntimeAnimationOverride(modelId) {
    return runtimeAnimationOverrides[modelId] || null;
}

/**
 * Declared authoring rate for a model's BUNDLED subclip ranges (Task #326).
 * Returns null when the entry doesn't declare one — callers then fall back
 * to inferring fps from the clip's key density (the historical behavior).
 * Deliberately does NOT fall back to AnimationConfig.default: an fps is a
 * statement about how a specific file's ranges were authored, never shared.
 */
export function getConfiguredFps(modelId) {
    const cfg = modelId ? AnimationConfig[modelId] : null;
    const f = cfg && cfg.fps;
    return Number.isFinite(f) && f >= 1 && f <= 120 ? f : null;
}

export function findAnimationClip(availableClips, modelId, animationType) {
    if (!availableClips || availableClips.length === 0) {
        console.error("No available clips provided");
        return null;
    }

    // Log available animations for debugging
    console.log(
        "Available clips:",
        availableClips.map((c) => c.name),
    );
    console.log("Looking for animation type:", animationType);
    console.log("Model ID:", modelId);

    // Try model-specific config first, then fall back to default
    const config = AnimationConfig[modelId] || AnimationConfig.default;
    if (!config) {
        console.error("No animation configuration found for model");
        return null;
    }

    let animConfig = config.animations[animationType];

    // Server override hook: ALL range resolution funnels through here, so this
    // single point covers every surface (/lab, /learn, courses, admin preview).
    const _ovr = runtimeAnimationOverrides[modelId];
    const _ovrRange = _ovr && _ovr.ranges && _ovr.ranges[animationType];
    if (
        _ovrRange &&
        Number.isFinite(_ovrRange.start) &&
        Number.isFinite(_ovrRange.end)
    ) {
        const names =
            (_ovr.names && _ovr.names[animationType]) ||
            (animConfig && animConfig.names) || ["ALL", animationType];
        animConfig = {
            names,
            subclip: { start: _ovrRange.start, end: _ovrRange.end },
        };
        console.log(
            `[AnimOverride] ${animationType}: server range ${_ovrRange.start}-${_ovrRange.end} overrides bundled config`,
        );
    }

    if (!animConfig) {
        console.error(`No ${animationType} animation configuration found`);
        return null;
    }

    // Try to find a matching animation with enhanced name matching
    let clip = null;
    let bestMatchScore = 0;
    let bestMatchClip = null;

    // First attempt: Try to find an exact match (case-insensitive)
    clip = availableClips.find((c) =>
        animConfig.names.some(
            (name) => c.name.toLowerCase() === name.toLowerCase(),
        ),
    );

    // If no exact match, try a more fuzzy approach
    if (!clip) {
        console.log(
            "No exact animation name match found, trying fuzzy matching...",
        );

        // Score each clip name based on similarity to our target animations
        for (const c of availableClips) {
            const clipName = c.name.toLowerCase();

            for (const targetName of animConfig.names) {
                const targetNameLower = targetName.toLowerCase();

                // Calculate a match score
                let score = 0;

                // Exact match gets highest score
                if (clipName === targetNameLower) {
                    score += 100;
                }
                // Contains the entire target string
                else if (clipName.includes(targetNameLower)) {
                    score += 75;
                }
                // Target contains the clip name
                else if (targetNameLower.includes(clipName)) {
                    score += 50;
                }
                // Word boundary matches (e.g. "idle_01" matches "idle")
                else if (
                    clipName.startsWith(targetNameLower + "_") ||
                    clipName.endsWith("_" + targetNameLower) ||
                    clipName.includes("_" + targetNameLower + "_")
                ) {
                    score += 40;
                }
                // Partial substring match
                else if (
                    clipName.includes(targetNameLower.substring(0, 4)) &&
                    targetNameLower.length >= 4
                ) {
                    score += 30;
                }

                // Update best match if this score is higher
                if (score > bestMatchScore) {
                    bestMatchScore = score;
                    bestMatchClip = c;
                }
            }
        }

        // If we found a good enough match, use it
        if (bestMatchScore >= 30) {
            console.log(
                `Found fuzzy match for ${animationType}: ${bestMatchClip.name} (score: ${bestMatchScore})`,
            );
            clip = bestMatchClip;
        }
    }

    if (!clip) {
        console.warn(
            `No matching ${animationType} animation found. Searched for:`,
            animConfig.names,
        );
        return null;
    }

    console.log(`Found ${animationType} animation:`, clip.name);
    return {
        clip,
        subclip: animConfig.subclip,
    };
}
